import type {
  Appointment,
  Customer,
  CostEntry,
  CustomerHistorySnapshot,
  LedgerEntry,
  MobileActivity,
  PackageRecord,
  PackagePlan,
  PackageRules,
  Payment,
  ReversalLog,
  Opportunity,
  GoalDailyEntry,
  StaffGoal,
  StaffMember,
  StudioClosureLog,
  TeamNote,
  TrainerException,
  TrainerSchedule,
  WorkTask,
  WorkspaceSettings,
  Workshop
} from "../types";

export interface StudioBootstrap {
  source: "thh";
  generatedAt: string;
  customers: Customer[];
  appointments: Appointment[];
  records: PackageRecord[];
  payments: Payment[];
  expenses: CostEntry[];
  opportunities: Opportunity[];
  ledger: LedgerEntry[];
  reversalLogs: ReversalLog[];
  studioClosureLogs: StudioClosureLog[];
  staff: StaffMember[];
  staffSchedules: TrainerSchedule[];
  scheduleExceptions: TrainerException[];
  teamNotes: TeamNote[];
  workTasks: WorkTask[];
  staffGoals: StaffGoal[];
  goalDailyEntries: GoalDailyEntry[];
  paymentMethods: Array<{ code: string; name: string }>;
  serviceTypes: Array<{ code: string; name: string; capacity: number }>;
  packagePlans: PackagePlan[];
  packageRules: PackageRules;
  workspaceSettings: WorkspaceSettings;
  workshops: Workshop[];
  workshopManagementReady: boolean;
  appointmentRange: { from: string; to: string };
}

export interface StudioPulse {
  source: "thh";
  generatedAt: string;
  appointments: Appointment[];
  mobileActivities: MobileActivity[];
  appointmentRange: { from: string; to: string };
}

export type StudioCommand =
  | "CREATE_CUSTOMER"
  | "UPDATE_CUSTOMER"
  | "CREATE_PACKAGE"
  | "UPDATE_PACKAGE"
  | "BURN_PACKAGE"
  | "CREATE_PAYMENT"
  | "VOID_PAYMENT"
  | "CREATE_APPOINTMENTS"
  | "CREATE_HOLD"
  | "CONFIRM_HOLD"
  | "CANCEL_HOLD"
  | "DELETE_HOLD"
  | "SET_APPOINTMENT_STATUS"
  | "CANCEL_APPOINTMENT_PROTECTED"
  | "CLOSE_STUDIO_DAY"
  | "EXTEND_STUDIO_CLOSURE_PACKAGES"
  | "VOID_APPOINTMENT"
  | "UPDATE_PACKAGE_RULES"
  | "UPDATE_PACKAGE_PLAN_PRICES"
  | "CREATE_EXPENSE"
  | "VOID_EXPENSE"
  | "CREATE_OPPORTUNITY"
  | "UPDATE_OPPORTUNITY"
  | "ARCHIVE_OPPORTUNITY"
  | "CREATE_NOTE"
  | "SET_NOTE_STATUS"
  | "CREATE_TASK"
  | "SET_TASK_STATUS"
  | "CREATE_GOAL"
  | "UPDATE_GOAL_PROGRESS"
  | "ADD_GOAL_DAILY_RESULT"
  | "CREATE_SCHEDULE_EXCEPTIONS"
  | "CANCEL_SCHEDULE_EXCEPTION"
  | "UPDATE_WORKSPACE_SETTINGS"
  | "GET_SMS_STATUS"
  | "SEND_SMS"
  | "SEND_STUDIO_ANNOUNCEMENT"
  | "CREATE_MOBILE_ACCOUNT"
  | "RESET_MOBILE_PASSWORD"
  | "CREATE_WORKSHOP"
  | "UPDATE_WORKSHOP"
  | "ARCHIVE_WORKSHOP"
  | "CANCEL_WORKSHOP_PARTICIPATION"
  | "REOPEN_APPOINTMENT"
  | "DELETE_PAYMENT";

export class StudioApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string
  ) {
    super(message);
  }
}

export function studioErrorCopy(error: unknown, fallback = "İşlem tamamlanamadı. Alanları kontrol edip yeniden deneyin.") {
  const code =
    error instanceof StudioApiError
      ? `${error.code} ${error.message}`.toUpperCase()
      : error instanceof Error
        ? error.message.toUpperCase()
        : "";
  if (code.includes("CONCURRENCY")) return "Bu kayıt başka bir cihazda veya sekmede değiştirilmiş. Güncel bilgiler yüklendi; son durumu kontrol edin.";
  if (code.includes("PROTECTEDCANCELLATION") || code.includes("PROTECTED_CANCELLATION")) return "Hak korumalı iptal altyapısı henüz hazır değil. 27 numaralı SQL yükseltme scriptini çalıştırın.";
  if (code.includes("APPOINTMENT_ALREADY_HAS_PACKAGE_MOVEMENT")) return "Bu randevu için daha önce paket hareketi oluşmuş. Sayfayı yenileyip güncel durumu kontrol edin.";
  if (code.includes("APPOINTMENT_MUST_BE_PLANNED_TO_CANCEL")) return "Yalnızca planlanan bir randevu iptal edilebilir. Güncel durumu kontrol edin.";
  if (code.includes("HOLD_DELETE_NOT_ALLOWED")) return "Seansa dönüştürülmüş bir bloke tamamen silinemez.";
  if (code.includes("HOLD_VERSION_INVALID")) return "Blokenin sürüm bilgisi geçersiz. Sayfayı yenileyip yeniden deneyin.";
  if (code.includes("APPOINTMENT_SLOT_EXCLUSIVE_PREFERENCE_CONFLICT")) return "Bu saatteki danışanlardan biri seansını başka bir danışanla paylaşmak istemiyor.";
  if (code.includes("APPOINTMENT_SLOT_MALE_COATTENDEE_CONFLICT")) return "Bu saatteki danışanlardan biri erkek danışanla aynı saatte olmak istemiyor.";
  if (code.includes("PACKAGE_EXPIRED")) return "Seçilen paketin kullanım süresi bu tarih için dolmuş.";
  if (code.includes("PACKAGE_EXPIRY_SELECTION_REQUIRED")) return "Paket için bir son kullanım tarihi seçilmelidir.";
  if (code.includes("PACKAGE_VALIDITY_SELECTION_INVALID")) return "Seçilen paket süresi güncel ayarlarla eşleşmiyor. Sayfayı yenileyip yeniden seçin.";
  if (code.includes("PACKAGE_CANCELLATION_ALLOWANCE_BELOW_USED")) return "İptal hakkı, daha önce kullanılmış iptal sayısının altına indirilemez.";
  if (code.includes("PACKAGE_EXPIRY_BEFORE_PLANNED_APPOINTMENT")) return "Paket bitiş tarihi, pakete bağlı planlı bir randevudan önce olamaz.";
  if (code.includes("PACKAGE_EXPIRY_EXTENSION_FAILED")) return "Paket bitiş tarihi uzatılamadı. Paketin güncel durumunu kontrol edin.";
  if (code.includes("PACKAGE_DATES_OR_CANCELLATION_INVALID")) return "Bitiş tarihi, seans adedi veya iptal hakkı geçerli aralıkta değil.";
  if (code.includes("STUDIO_DAY_ALREADY_CLOSED")) return "Bu tarih daha önce toplu kapanışla işlendi. Aynı randevular yeniden iptal edilmedi.";
  if (code.includes("STUDIO_DAY_NOT_CLOSED")) return "Önce seçilen tarih için “Tüm randevuları iptal et” işlemini tamamlayın.";
  if (code.includes("STUDIO_CLOSURE_PACKAGES_ALREADY_EXTENDED")) return "Bu kapanışa bağlı paketler daha önce uzatılmış. İkinci kez süre eklenmedi.";
  if (code.includes("STUDIO_CLOSURE_HAS_NO_PACKAGES_TO_EXTEND")) return "Bu kapanışta uzatılabilecek aktif bir paket bulunamadı.";
  if (code.includes("NOT_ENOUGH_AVAILABLE") || code.includes("NOT_ENOUGH_REMAINING")) return "Pakette bu işlem için yeterli kullanılabilir seans hakkı kalmadı.";
  if (code.includes("PACKAGE_NOT_ACTIVE")) return "Bu paket artık aktif değil.";
  if (code.includes("PAYMENT_EXCEEDS")) return "Ödeme, paketin kalan borcundan daha yüksek olamaz.";
  if (code.includes("PACKAGE_PRICE_BELOW_PAID_AMOUNT")) return "Yeni son fiyat, bu paket için daha önce tahsil edilen toplamdan düşük olamaz.";
  if (code.includes("PHONE_ALREADY_EXISTS")) return "Bu telefon numarası başka bir aktif müşteride kayıtlı.";
  if (code.includes("PHONE_INVALID")) return "Telefon numarası 05xx xxx xx xx biçiminde olmalı veya boş bırakılmalı.";
  if (code.includes("MOBILE_ACCOUNT_EMAIL_REQUIRED")) return "Mobil erişim oluşturmak için müşteri kartına geçerli bir e-posta ekleyin.";
  if (code.includes("MOBILE_ACCOUNT_EMAIL_ALREADY_EXISTS")) return "Bu e-posta başka bir mobil uygulama hesabında kullanılıyor.";
  if (code.includes("MOBILE_ACCOUNT_ALREADY_EXISTS")) return "Bu müşterinin mobil uygulama hesabı zaten bulunuyor.";
  if (code.includes("MOBILE_ACCOUNT_NOT_FOUND")) return "Bu müşteriye bağlı aktif mobil uygulama hesabı bulunamadı.";
  if (code.includes("MOBILE_ACCOUNT_CONCURRENCY_CONFLICT")) return "Mobil hesap başka bir işlemde güncellendi. Güncel bilgileri yükleyip yeniden deneyin.";
  if (code.includes("MOBILE_CREDENTIAL_SECRET_UNAVAILABLE")) return "Geçici şifre güvenli biçimde üretilemedi. Backend yapılandırmasını kontrol edin.";
  if (code.includes("CUSTOMER_HISTORY_RESPONSE_INVALID")) return "Müşteri özeti servisi henüz güncellenmemiş. Backend yayınını tamamlayıp yeniden deneyin.";
  if (code.includes("HOLD_CUSTOMER_SLOT_ALREADY_EXISTS")) return "Bu müşteri için aynı saatte açık bir bloke zaten var.";
  if (code.includes("APPOINTMENT_CUSTOMER_SLOT_ALREADY_EXISTS")) return "Bu müşterinin aynı tarih ve saatte zaten açık bir seansı veya blokesi var.";
  if (code.includes("DATE_OR_CUSTOMER_INVALID")) return "Müşteri, tarih veya saat bilgisi geçerli değil.";
  if (code.includes("PACKAGE_QUANTITY_BELOW_PLANNED")) return "Seans adedi mevcut planlı seansların altına indirilemez.";
  if (code.includes("FINANCE_PIN_INVALID")) return "Kasa yetki kodu doğru değil.";
  if (code.includes("FINANCE_PIN_RATE_LIMITED")) return "Art arda çok sayıda hatalı kasa kodu girildi. Bir süre bekleyip tekrar deneyin.";
  if (code.includes("FINANCE_PIN_NOT_CONFIGURED")) return "Kasa yetki kodu Azure ayarlarında yapılandırılmamış.";
  if (code.includes("PRICE_CATALOG_PLAN_NOT_FOUND")) return "Fiyatı değiştirilen paket artık aktif değil. Güncel listeyi yükleyip tekrar deneyin.";
  if (code.includes("PRICE_CATALOG_VALUE_INVALID") || code.includes("PRICE_CATALOG_CHANGE_COUNT_INVALID")) return "Fiyat listesinde geçersiz bir tutar var. Tüm fiyatların sıfırdan büyük olduğunu kontrol edin.";
  if (code.includes("PRICE_CHANGED")) return "Fiyat kataloğu değişmiş. Güncel fiyat yüklendi; paketi yeniden kontrol edin.";
  if (code.includes("LOYALTY_DISCOUNT_NOT_ELIGIBLE")) return "Bu müşteri henüz sadakat indirimine uygun değil.";
  if (code.includes("PACKAGE_PAYMENT_METHOD_INVALID")) return "Seçilen tahsilat yöntemi artık aktif değil. Güncel yöntemi seçip yeniden deneyin.";
  if (code.includes("CATALOG_PLAN_NOT_FOUND")) return "Seçilen paket fiyat kataloğunda artık aktif değil.";
  if (code.includes("PRICE") || code.includes("DISCOUNT")) return "Paket fiyatı veya indirim seçimi fiyat kataloğuyla uyuşmuyor.";
  if (code.includes("WORKSHOP_SESSION_COST_INVALID")) return "Atölye hak maliyeti 1 ile 100 arasında tam sayı olmalıdır.";
  if (code.includes("WORKSHOP_SESSION_COST_LOCKED")) return "İlk katılımdan sonra atölyenin hak maliyeti değiştirilemez.";
  if (code.includes("WORKSHOP_PACKAGE_REQUIRED")) return "Atölyeye katılmak için bir paket seçilmelidir.";
  if (code.includes("WORKSHOP_PACKAGE_NOT_OWNED")) return "Seçilen paket bu müşteriye ait değil.";
  if (code.includes("WORKSHOP_PACKAGE_INACTIVE")) return "Seçilen paket artık aktif değil.";
  if (code.includes("WORKSHOP_PACKAGE_EXPIRES_BEFORE_EVENT")) return "Seçilen paketin süresi atölye tarihinden önce doluyor.";
  if (code.includes("WORKSHOP_PACKAGE_RIGHTS_INSUFFICIENT")) return "Seçilen pakette atölye için yeterli hak bulunmuyor.";
  if (code.includes("WORKSHOP_ALREADY_REGISTERED")) return "Müşteri bu atölyeye zaten kayıtlı.";
  if (code.includes("WORKSHOP_FULL")) return "Atölye kontenjanı dolu.";
  if (code.includes("WORKSHOP_REGISTRATION_CLOSED")) return "Atölyenin kayıt süresi sona ermiş.";
  if (code.includes("WORKSHOP_PARTICIPATION_NOT_FOUND")) return "Atölye katılım kaydı bulunamadı.";
  if (code.includes("WORKSHOP_PARTICIPATION_VERSION_CONFLICT")) return "Katılım başka bir işlemde değişti. Güncel bilgiler yüklendi.";
  if (code.includes("WORKSHOP_PARTICIPATION_ALREADY_CANCELLED")) return "Bu katılım daha önce iptal edilmiş; ikinci kez hak iadesi yapılmadı.";
  if (code.includes("WORKSHOP_CANCELLATION_REASON_REQUIRED")) return "İptal gerekçesi zorunludur.";
  if (code.includes("WORKSHOP_REGISTRATION_NON_CANCELLABLE")) return "Bu atölye kaydı mobil uygulamadan iptal edilemez.";
  if (code.includes("WORKSHOP_NOT_FOUND_OR_CONCURRENCY") || code.includes("WORKSHOP_VERSION_INVALID")) return "Atölye başka bir işlemde güncellendi. Güncel bilgileri kontrol edip yeniden deneyin.";
  if (code.includes("WORKSHOP_CAPACITY_BELOW_REGISTRATIONS")) return "Kapasite mevcut kayıtlı katılımcı sayısından düşük olamaz.";
  if (code.includes("WORKSHOP_EVENT_DATE_MUST_BE_FUTURE")) return "Yayındaki bir atölyenin başlangıç tarihi gelecekte olmalıdır.";
  if (code.includes("WORKSHOP_REGISTRATION_CLOSE_INVALID")) return "Son kayıt tarihi atölye başlangıcından sonra olamaz.";
  if (code.includes("WORKSHOP_END_DATE_INVALID")) return "Bitiş tarihi başlangıç tarihinden sonra olmalıdır.";
  if (code.includes("WORKSHOP_IMAGE_URL_INVALID")) return "Görsel bağlantısı https:// ile başlayan geçerli bir adres olmalıdır.";
  if (code.includes("WORKSHOP_")) return "Atölye bilgileri geçerli değil. Tarih, kapasite ve fiyat alanlarını kontrol edin.";
  return fallback;
}

async function readJson<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const code =
      typeof (body as { error?: unknown })?.error === "string"
        ? String((body as { error: string }).error)
        : "REQUEST_FAILED";
    const detail =
      typeof (body as { detail?: unknown })?.detail === "string"
        ? String((body as { detail: string }).detail)
        : code;
    if (
      response.status === 401 &&
      code === "PORTAL_SESSION_REQUIRED" &&
      typeof window !== "undefined" &&
      window.location.pathname !== "/login"
    ) {
      window.location.replace("/login");
    }
    throw new StudioApiError(detail, response.status, code);
  }
  return body as T;
}

function normalizeSqlRowVersion(value: unknown) {
  const text = typeof value === "string" ? value : "";
  const hex = text.replace(/^0x/i, "");
  if (/^[0-9a-f]{16}$/i.test(hex)) return hex.toUpperCase();

  const toHex = (bytes: number[]) =>
    bytes
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("")
      .padStart(16, "0")
      .toUpperCase();
  const binaryBytes = (candidate: string) => {
    const bytes = Array.from(candidate, (character) => character.codePointAt(0) ?? -1);
    return bytes.length > 0 && bytes.length <= 8 && bytes.every((byte) => byte >= 0 && byte <= 0xff)
      ? bytes
      : null;
  };

  const directBytes = binaryBytes(text);
  if (directBytes) return toHex(directBytes);

  // SQL sürücüsü bazı yüksek rowversion baytlarını Windows-1252 karakterleri
  // olarak döndürebiliyor. Karakterleri yeniden UTF-8 çözmeden özgün baytlara çevir.
  const windows1252Extensions = new Map<number, number>([
    [0x20ac, 0x80], [0x201a, 0x82], [0x0192, 0x83], [0x201e, 0x84],
    [0x2026, 0x85], [0x2020, 0x86], [0x2021, 0x87], [0x02c6, 0x88],
    [0x2030, 0x89], [0x0160, 0x8a], [0x2039, 0x8b], [0x0152, 0x8c],
    [0x017d, 0x8e], [0x2018, 0x91], [0x2019, 0x92], [0x201c, 0x93],
    [0x201d, 0x94], [0x2022, 0x95], [0x2013, 0x96], [0x2014, 0x97],
    [0x02dc, 0x98], [0x2122, 0x99], [0x0161, 0x9a], [0x203a, 0x9b],
    [0x0153, 0x9c], [0x017e, 0x9e], [0x0178, 0x9f]
  ]);
  const encodedBytes = Array.from(text, (character) => {
    const codePoint = character.codePointAt(0) ?? -1;
    return codePoint <= 0xff ? codePoint : (windows1252Extensions.get(codePoint) ?? -1);
  });
  if (encodedBytes.length > 0 && encodedBytes.length <= 8 && encodedBytes.every((byte) => byte >= 0)) {
    return toHex(encodedBytes);
  }

  return text;
}

function normalizeCommandPayload(action: StudioCommand, payload: unknown) {
  if (!payload || typeof payload !== "object") {
    return payload;
  }

  if (
    action === "DELETE_HOLD" ||
    action === "RESET_MOBILE_PASSWORD" ||
    action === "UPDATE_WORKSHOP" ||
    action === "ARCHIVE_WORKSHOP" ||
    action === "CANCEL_WORKSHOP_PARTICIPATION"
  ) {
    const versionedPayload = payload as { version?: unknown };
    return {
      ...versionedPayload,
      version: normalizeSqlRowVersion(versionedPayload.version)
    };
  }

  if (action !== "UPDATE_PACKAGE_PLAN_PRICES") return payload;

  const pricingPayload = payload as {
    prices?: Array<{ id: string; version: unknown; defaultPrice: number }>;
  };
  if (!Array.isArray(pricingPayload.prices)) return payload;

  return {
    ...pricingPayload,
    prices: pricingPayload.prices.map((price) => ({
      ...price,
      version: normalizeSqlRowVersion(price.version)
    }))
  };
}

export const studioApi = {
  async bootstrap(
    range: { from: string; to: string },
    signal?: AbortSignal,
    includeLedger = false
  ) {
    const query = new URLSearchParams({
      appointmentsFrom: range.from,
      appointmentsTo: range.to,
      includeLedger: includeLedger ? "1" : "0"
    });
    const response = await fetch(`/api/studio?${query}`, {
      method: "GET",
      cache: "no-store",
      headers: { accept: "application/json" },
      signal
    });
    return readJson<StudioBootstrap>(response);
  },

  async pulse(
    range: { from: string; to: string },
    signal?: AbortSignal
  ) {
    const query = new URLSearchParams({
      view: "pulse",
      appointmentsFrom: range.from,
      appointmentsTo: range.to
    });
    const response = await fetch(`/api/studio?${query}`, {
      method: "GET",
      cache: "no-store",
      headers: { accept: "application/json" },
      signal
    });
    return readJson<StudioPulse>(response);
  },

  async customerHistory(customerId: string, signal?: AbortSignal) {
    const query = new URLSearchParams({
      view: "customerHistory",
      customerId
    });
    const response = await fetch(`/api/studio?${query}`, {
      method: "GET",
      cache: "no-store",
      headers: { accept: "application/json" },
      signal
    });
    const body = await readJson<unknown>(response);
    if (
      !body ||
      typeof body !== "object" ||
      !("customer" in body) ||
      !Array.isArray((body as { packages?: unknown }).packages) ||
      !Array.isArray((body as { events?: unknown }).events)
    ) {
      throw new StudioApiError(
        "CUSTOMER_HISTORY_RESPONSE_INVALID",
        502,
        "CUSTOMER_HISTORY_RESPONSE_INVALID"
      );
    }
    return body as CustomerHistorySnapshot;
  },

  async command<T = unknown>(
    action: StudioCommand,
    payload: unknown,
    idempotencyKey: string = crypto.randomUUID(),
    financePin?: string
  ) {
    const headers: Record<string, string> = {
      accept: "application/json",
      "content-type": "application/json"
    };
    if (financePin) headers["x-thh-finance-pin"] = financePin;
    const response = await fetch("/api/studio", {
      method: "POST",
      headers,
      body: JSON.stringify({
        action,
        payload: normalizeCommandPayload(action, payload),
        idempotencyKey
      })
    });
    return readJson<{ ok: true; result: T }>(response);
  }
};

export interface CreateAppointmentRequest {
  customerId: string;
  recordId: string;
  date: string;
  time: string;
  trainer: string;
  note?: string;
  repeatCount?: number;
}

export const appointmentApi = {
  async create(input: CreateAppointmentRequest) {
    return studioApi.command<{ appointmentIds: string[] }>("CREATE_APPOINTMENTS", input);
  }
};
